import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { Box, Typography, Button, Paper, Divider, Chip, Stack } from '@mui/material';
import Grid from '@mui/material/Grid';
import { styled } from '@mui/material/styles';

// Icons
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import PrintIcon from '@mui/icons-material/Print';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DownloadIcon from '@mui/icons-material/Download';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

// Thunks and Services
import {
  storeDownloadContractPdf,
  storeUpdateEmployeeContractPdf,
  storeDownloadPdfEmployee,
} from '../../../../../../store/employee/thunks';
import { uploadPdfStore } from '../../../../../../store/uploaders/thunks';
import { useResponseHandler } from 'hooks/useResponseHandler';
import { ModalViewPdfGlobal } from 'components/modals/viwerPdf/ModalViewPdfGlobal';

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

export const DetalleContratoyFormularios = ({ dataEmployee }: any) => {
  const dispatch = useDispatch();
  const { ResponseComponents, handleResponse } = useResponseHandler();

  const [hasSignedContract, setHasSignedContract] = useState(false);
  const [signedFileName, setSignedFileName] = useState<string | null>(null);

  const [showPdfDetail, setShowPdfDetail] = useState(false);
  const [urlRutPdf, setUrlRutPdf] = useState<string | null>(null);

  useEffect(() => {
    if (dataEmployee?.file_signed_contract_name) {
      setHasSignedContract(true);
      setSignedFileName(dataEmployee.file_signed_contract_name);
    }
  }, [dataEmployee]);

  const handleGeneratePDF = async () => {
    if (dataEmployee?.id) {
      const result = await dispatch(storeDownloadContractPdf(dataEmployee.id));
      if (result) {
        const url = window.URL.createObjectURL(new Blob([result], { type: 'application/pdf' }));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `contrato_${dataEmployee.name || dataEmployee.id}.pdf`);
        document.body.appendChild(link);
        link.click();
        link.remove();
      }
    }
  };

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0 && dataEmployee?.id) {
      const file = e.target.files[0];

      // 1. Upload the file to the file server using Redux Thunk
      const uploadResult = await dispatch(
        uploadPdfStore('contrato-empleado', file as any, () => {})
      );
      await handleResponse(uploadResult, 'Subir contrato', false);

      if (uploadResult?.ok) {
        const filename = uploadResult.data.pdfName;
        // 2. Save the filename in the employee entity
        const updateResult = await dispatch(
          storeUpdateEmployeeContractPdf(dataEmployee.id, filename)
        );
        await handleResponse(updateResult, 'Actualizar contrato firmado', true);

        if (updateResult?.ok) {
          setHasSignedContract(true);
          setSignedFileName(filename);
        }
      }
    }
  };

  const handleOpenPdf = () => {
    if (signedFileName) {
      setUrlRutPdf(signedFileName);
      setShowPdfDetail(true);
    }
  };

  return (
    <Box sx={{ p: 3, animation: 'fadeIn 0.5s ease-in-out' }}>
      {ResponseComponents}

      {showPdfDetail && (
        <ModalViewPdfGlobal
          open={showPdfDetail}
          onClose={() => setShowPdfDetail(false)}
          pdfUrl={urlRutPdf}
          typeUrl="contrato-empleado"
        />
      )}

      <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#192a56', mb: 3 }}>
        Expediente Digital y Contratos
      </Typography>

      <Grid container spacing={3}>
        {/* TARJETA DE CONTRATO LABORAL */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper
            elevation={3}
            sx={{ p: 3, borderRadius: 3, borderTop: '4px solid #192a56', height: '100%' }}
          >
            <Box
              sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <PictureAsPdfIcon sx={{ color: '#e15f41', fontSize: 40 }} />
                <Typography variant="h6" sx={{ fontWeight: '600' }}>
                  Contrato Laboral
                </Typography>
              </Box>
              {hasSignedContract ? (
                <Chip
                  icon={<CheckCircleIcon />}
                  label="Firmado y Subido"
                  color="success"
                  variant="outlined"
                />
              ) : (
                <Chip
                  icon={<ErrorOutlineIcon />}
                  label="Falta Firma"
                  color="warning"
                  variant="outlined"
                />
              )}
            </Box>

            <Divider sx={{ my: 2 }} />

            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Genera el contrato pre-llenado con los datos actuales del empleado para su impresión.
              Una vez firmado físicamente, súbelo al expediente digital como respaldo legal.
            </Typography>

            <Stack spacing={2} direction="column">
              <Button
                variant="contained"
                color="primary"
                startIcon={<PrintIcon />}
                onClick={handleGeneratePDF}
                sx={{
                  bgcolor: '#192a56',
                  '&:hover': { bgcolor: '#273c75' },
                  borderRadius: 2,
                  py: 1,
                  textTransform: 'none',
                  fontWeight: 'bold',
                  boxShadow: 2,
                }}
              >
                Generar e Imprimir Contrato
              </Button>

              <Stack direction="row" spacing={2}>
                <Button
                  component="label"
                  variant={hasSignedContract ? 'outlined' : 'contained'}
                  startIcon={<CloudUploadIcon />}
                  sx={{ flex: 1, borderRadius: 2, textTransform: 'none', fontWeight: 'bold' }}
                  color={hasSignedContract ? 'success' : 'primary'}
                >
                  {hasSignedContract ? 'Actualizar Contrato' : 'Subir Firmado'}
                  <VisuallyHiddenInput type="file" accept=".pdf" onChange={handleUpload} />
                </Button>

                <Button
                  variant="outlined"
                  color="secondary"
                  disabled={!hasSignedContract}
                  onClick={handleOpenPdf}
                  startIcon={<PictureAsPdfIcon />}
                  sx={{ flex: 1, borderRadius: 2, textTransform: 'none', fontWeight: 'bold' }}
                >
                  Ver Contrato
                </Button>
              </Stack>
            </Stack>
          </Paper>
        </Grid>

        {/* TARJETA OTROS FORMULARIOS */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              borderRadius: 3,
              border: '2px dashed #7f8fa6',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              bgcolor: '#f5f6fa',
              transition: 'all 0.3s ease',
              '&:hover': { borderColor: '#192a56', bgcolor: '#fdfdfd' },
            }}
          >
            <CloudUploadIcon sx={{ fontSize: 50, color: '#b2bec3', mb: 1 }} />
            <Typography variant="subtitle1" color="text.secondary" fontWeight="bold">
              Otros Documentos Adicionales
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              textAlign="center"
              sx={{ mt: 1, maxWidth: '80%' }}
            >
              Próximamente: gestiona aquí acuerdos de confidencialidad, políticas de la empresa y
              anexos al contrato.
            </Typography>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};
